import { z } from 'zod';
import { addressDocumentSchema } from './address';
import { paymentDocumentSchema } from './payment';
// GB #1719 (2026-08-16): import from within the package, not across into
// src/. The package must be self-contained — a relative `../../../src/`
// import broke apps/mobile bundling and any consumer without GearFocus
// web's src/ tree on disk. carrierCodeSchema's canonical home is now
// packages/types/carrierCode.ts; src/utils/maps/carrierCode.ts re-exports
// it so existing web consumers keep working unmodified.
import { carrierCodeSchema } from '../carrierCode';

export const OrderStates = [
  'Awaiting Shipment',
  'In Transit',
  'Delivered',
  'Complete',
  'Cancelled',
  'Refunded',
  'Delivery Error',
  'On Hold',
  'Processing',
] as const;

export type OrderStateType = (typeof OrderStates)[number];

export const OrderNoteSchema = z
  .object({
    text: z.string(),
    created: z.number().optional().default(() => Date.now()),
    updated: z.date().optional(),
  })
  .optional();

export type OrderNoteType = z.infer<typeof OrderNoteSchema>;

export const orderItemDocumentSchema = z.object({
  id: z.string(),
  qty: z.number(),
  state: z.number().min(0).max(12), //0-Awaiting Shipment, 1-In Transit, 2-Delivered, 3-Complete, 4-Cancelled (Seller Couldn't fulfill Order), 5-Refunded (Buyer asks, we issue refund), 6-Delivery Error, 7-On Hold, 8-Processing
  product_cost: z.number(),
  product_tax: z.number().optional(),
  refund_amount: z.number().optional(),
  refund_reason: z.string().optional(),
  buyer_shipping_cost: z.number(),
  account_id: z.string().optional(),
  delivery_time: z.number().optional(),
  notified_time: z
    .object({
      first: z.number().optional(),
      second: z.number().optional(),
      third: z.number().optional(),
    })
    .optional(), // 96, 168, 240 hours => 4, 7, 10 days
  insurance_amount: z.number().optional(),
  seller_shipping_cost: z.number().optional(),
  label_cost: z.number().optional(),
  ship_engine_pi: z.string().optional(),
  label_id: z.string().optional(),
  carrier: carrierCodeSchema.optional(),
  tracking_number: z.string().optional(),
  label_pdf: z.string().optional(),
  label_png: z.string().optional(),
  rate_id: z.string().nullable().optional(),
  seller_id: z.string().optional(),
  variations: z
    .object({
      letter: z.string().optional(),
      number: z.number().optional(),
    })
    .optional(),
  // Product details for analytics tracking (optional for backwards compatibility)
  product_title: z.string().optional(),
  product_sku: z.string().optional(),
  product_brand: z.string().optional(),
  product_image: z.string().optional(),
  product_categories: z.array(z.string()).optional(),
  external_source: z.string().optional(),
})
  // web#1676: the api writes payout/refund-lock fields onto order items
  // (transfer_id, payout_lock, refund_lock, and others — see PayableItem in
  // gearfocus-api's payoutExecutor.ts) that this schema never declared. Zod
  // strips unknown keys by default, so getOrderById() silently erased them
  // from memory, and updateOrderItemState()/markAsDelivered() then wrote the
  // whole stripped item back to Firestore — permanently deleting the
  // never-expiring double-pay guard (transfer_id) any time an admin touched
  // an order's state. .passthrough() stops the strip without asserting a
  // type for fields we cannot validate against live data: 9 of the 13 known
  // fields have never been observed on a real document, so a wrong declared
  // type would make the read-side parse throw instead — getOrderById()
  // returns null on a parse failure, which 404s a real buyer on the
  // post-purchase order-confirmation page. (transfer_id/paid_at/paid_via are
  // the exception: those three are simply young — live since 2026-07-31 —
  // and do have real documents to validate a type against.)
  .passthrough()
  // web#1676 adversarial finding: payout_lock and refund_lock must NOT
  // round-trip through passthrough, unlike everything else above. They are
  // TRANSIENT BY CONSTRUCTION — set only while a transfer/refund is actually
  // in flight, deleted by the api the instant it settles (payoutExecutor.ts,
  // refundOrder.ts:794) — and nothing in the api ever reads their age or
  // expires them (grepped for TTL/expiry/reaper logic: none exists). A client
  // that reads a lock, holds it in memory past the moment it clears
  // server-side, then writes the WHOLE sellers map back later (exactly what
  // updateOrderItemState/markAsDelivered do) resurrects a lock with no live
  // transfer behind it — and nothing ever clears a resurrected one. Direction
  // matters: today's strip erroneously CLEARS an in-flight lock, which errs
  // toward a seller getting paid; preserving it would err toward a seller
  // being permanently, silently frozen out of payout (isPayoutEligible and
  // hasPayoutInFlight both treat any present lock as blocking, forever,
  // fixable only by manual Firestore surgery). That is the worse failure
  // mode, so these two keys are explicitly dropped post-parse rather than
  // passed through. Every other field passthrough preserves is either
  // durable (transfer_id) or has no live consumer that treats its mere
  // presence as an active guard.
  .transform(({ payout_lock, refund_lock, ...rest }) => rest);

export const orderDocumentSchema = z.object({
  address: addressDocumentSchema,
  buyer_id: z.string(),
  buyer_email: z.string().optional(),
  coupon: z
    .object({
      id: z.string(),
      code: z.string().optional(),
      name: z.string().optional(),
      amount_off: z.number().optional().nullable(),
      percent_off: z.number().optional().nullable(),
      off: z.number().optional(),
    })
    .optional(),
  customer_id: z.string().optional(),
  created: z.number().default(() => Date.now()),
  donate: z.boolean().default(false),
  from_web: z.boolean().default(true),
  has_promo: z.boolean().default(false),
  id: z.string(),
  is_offer: z.boolean().default(false),
  is_auction: z.boolean().default(false),
  item_count: z.number().default(1),
  notes: z.array(OrderNoteSchema).optional(),
  order_num: z.union([z.string(), z.number()]),
  payment: paymentDocumentSchema,
  payment_id: z.string().optional().default(''),
  product_ids: z.array(z.string()).optional(),
  reviewed_sellers: z.array(z.string()).optional().default([]),
  reviewed_buyers: z.array(z.string()).optional().default([]),
  seller_arr: z.array(z.string()).default([]),
  sellers: z.object({}).catchall(z.array(orderItemDocumentSchema)).optional(), //TODO: remove optional
  shipping_total: z.number(),
  subtotal: z.number(),
  tax: z.number().optional(),
  tns: z.array(z.string()).optional().default([]),
  total: z.number(),
  total_state: z.number().min(0).max(5).default(0), // 0-Order Placed 1-Delivered 2-Complete, 3- On Hold, 4- Return Requested
  tracking_error: z.number().optional(),
  updated: z.number().optional(),
  states: z.array(z.number()).optional(),
  analytics_fired: z.boolean().optional(),
  tariff_acknowledgment: z.object({
    acknowledged: z.boolean(),
    acknowledged_at: z.number(),
    buyer_country: z.string(),
    seller_countries: z.array(z.string()),
    disclosure_text: z.string(),
  }).optional(),
});
// web#1676: the same class of gap exists one level up — financials,
// attribution, seller_states, offer_id, and refund_payout_holds all exist on
// live order documents but were never declared here, so they vanish from
// memory on every read through this schema. DELIBERATELY LEFT STRIPPED, not
// an oversight — read this before "completing" the fix by adding
// .passthrough() here too:
//
// 1. It is LATENT, not live. All 18 write sites touching the `orders`
//    collection across both repos were audited for this change, and every
//    one names a narrow key list — none round-trips a parsed order back.
//    Nothing erases these fields today.
// 2. Passthrough would make it WORSE, not better. Both order-confirmation
//    pages (order-confirmation-v3/[id].tsx, order-confirmation/[id].tsx) hand
//    the parsed order straight into getServerSideProps, and Next.js embeds
//    those props in __NEXT_DATA__ in the buyer's page HTML. `financials`
//    contains our per-order Stripe processing cost (`stripe_fee`) and
//    `attribution` contains referrer/landing_page/first_touch. Right now Zod
//    strips them before they ever reach that props object, so they never
//    reach page source. Passthrough here would put our own fee data in front
//    of every buyer within the hour of purchase — see
//    MAX_ORDER_AGE_MS in confirmationOrder.ts, which is exactly why the
//    item-level passthrough above is safe (transfer_id only exists days
//    later, long after the page stops serving) and this one is not.
// 3. Closing this gap properly needs a fix that keeps these fields
//    server-side — e.g. a separate parse for admin/internal callers vs. a
//    stripped-down props shape for the confirmation pages — not a schema
//    change that reaches every caller identically. That is future work, not
//    this PR.

export type OrderItemDocument = z.infer<typeof orderItemDocumentSchema>;
export type OrderDocument = z.infer<typeof orderDocumentSchema>;
